import React from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';

const ThankYou: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const status = searchParams.get('status');

  // Get submission details from navigation state
  const { platform, handle, email, websiteUrl, fromSignup } = location.state || {};

  return (
    <div style={{
      minHeight: '100vh',
      background: '#ffffff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      <div style={{
        maxWidth: '600px',
        textAlign: 'center',
        background: 'white',
        borderRadius: '12px',
        padding: '3rem 2rem',
        border: '1px solid #e1e8ed'
      }}>
        {/* Status Icon */}
        <div style={{
          width: '80px',
          height: '80px',
          margin: '0 auto 2rem',
          background: status === 'approved' ? 'linear-gradient(135deg, #4CD964 0%, #3CB54C 100%)' :
                     status === 'rejected' ? 'linear-gradient(135deg, #FF6B6B 0%, #FF4444 100%)' :
                     'linear-gradient(135deg, #FFD93D 0%, #FFB344 100%)',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: status === 'approved' ? '0 4px 20px rgba(76, 217, 100, 0.3)' :
                     status === 'rejected' ? '0 4px 20px rgba(255, 107, 107, 0.3)' :
                     '0 4px 20px rgba(255, 217, 61, 0.3)'
        }}>
          <svg
            width="40"
            height="40"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            {status === 'approved' ? (
              <path
                d="M20 6L9 17L4 12"
                stroke="white"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            ) : status === 'rejected' ? (
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="white"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            ) : (
              <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="3" fill="none" />
            )}
          </svg>
        </div>

        {/* Thank You Message */}
        <h1 style={{
          fontSize: '2rem',
          fontWeight: 'bold',
          color: '#333',
          marginBottom: '1rem'
        }}>
          {fromSignup ? 'Welcome to Dstribute! 🎉' :
           status === 'approved' ? 'Congratulations! 🎉' :
           status === 'rejected' ? 'Thank You for Your Interest' :
           'Thank You! 📝'}
        </h1>

        <p style={{
          fontSize: '1.1rem',
          color: '#555',
          lineHeight: '1.8',
          marginBottom: '1rem'
        }}>
          {fromSignup ?
            'Your account has been created successfully! We\'re excited to help you grow your business with influencer marketing.' :
           status === 'approved' ?
            'Great news! Your collaboration has been automatically approved. We\'re excited to work with you!' :
           status === 'rejected' ?
            'We appreciate your interest in collaborating with us. Unfortunately, your submission doesn\'t meet our current requirements.' :
            'Your collaboration inquiry has been submitted successfully. We will review your submission and get back to you soon.'}
        </p>

        {/* Show submission details if available */}
        {(platform || handle || email || websiteUrl) && (
          <div style={{
            background: '#f0f8ff',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '2rem',
            border: '1px solid #d4e6f1'
          }}>
            <p style={{
              fontSize: '0.9rem',
              color: '#666',
              margin: '0 0 0.5rem 0'
            }}>
              <strong>{fromSignup ? 'Account Details:' : 'Submission Details:'}</strong>
            </p>
            {email && (
              <p style={{ fontSize: '0.9rem', color: '#555', margin: '0.25rem 0' }}>
                ✉️ Email: <strong>{email}</strong>
              </p>
            )}
            {websiteUrl && (
              <p style={{ fontSize: '0.9rem', color: '#555', margin: '0.25rem 0' }}>
                🌐 Website: <strong>{websiteUrl}</strong>
              </p>
            )}
            {platform && (
              <p style={{ fontSize: '0.9rem', color: '#555', margin: '0.25rem 0' }}>
                📱 Platform: <strong>{platform.charAt(0).toUpperCase() + platform.slice(1)}</strong>
              </p>
            )}
            {handle && (
              <p style={{ fontSize: '0.9rem', color: '#555', margin: '0.25rem 0' }}>
                👤 Handle: <strong>@{handle}</strong>
              </p>
            )}
          </div>
        )}

        {/* Additional Info */}
        <div style={{
          background: '#f8f9fa',
          borderRadius: '8px',
          padding: '1.5rem',
          marginBottom: '2rem',
          border: '1px solid #e9ecef'
        }}>
          <h3 style={{
            fontSize: '1rem',
            fontWeight: '600',
            color: '#333',
            marginBottom: '0.75rem'
          }}>
            What happens next?
          </h3>
          <ul style={{
            textAlign: 'left',
            color: '#666',
            fontSize: '0.95rem',
            lineHeight: '1.8',
            paddingLeft: '1.5rem',
            margin: 0
          }}>
            {fromSignup ? (
              <>
                <li>Our team will analyze your website and identify relevant influencers</li>
                <li>We'll start reaching out to potential creators for collaboration</li>
                <li>You'll receive updates on campaign progress via email</li>
              </>
            ) : status === 'approved' ? (
              <>
                <li>You'll receive an email with collaboration details shortly</li>
                <li>Our team will contact you to finalize the content plan</li>
                <li>Once confirmed, we'll proceed with the collaboration</li>
              </>
            ) : status === 'rejected' ? (
              <>
                <li>Thank you for your interest in collaborating with us</li>
                <li>Feel free to apply again in the future</li>
                <li>Follow us for future collaboration opportunities</li>
              </>
            ) : (
              <>
                <li>Our team will review your submission within 24 hours</li>
                <li>We'll send you an email with our collaboration proposal</li>
              </>
            )}
          </ul>
        </div>

        {/* Action Button */}
        <div style={{
          display: 'flex',
          justifyContent: 'center'
        }}>
          <button
            onClick={() => navigate('/')}
            style={{
              padding: '0.75rem 2.5rem',
              background: '#ff6b35',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 15px rgba(255, 107, 53, 0.3)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#ff5722';
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(255, 107, 53, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#ff6b35';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 15px rgba(255, 107, 53, 0.3)';
            }}
          >
            Go to Homepage
          </button>
        </div>
      </div>
    </div>
  );
};

export default ThankYou;